// Israeli Architectural Plans Service
// Integrates with GovMap open data and iplan.gov.il to get real architectural plans

import config from '../config/api';
import LandUseService from './LandUseService';
import GovMapService from './GovMapService';
import proj4 from 'proj4';

const ITM_DEF = '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs';
const WEB_MERCATOR_DEF = '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs';

proj4.defs('EPSG:2039', ITM_DEF);
proj4.defs('EPSG:3857', WEB_MERCATOR_DEF);

// OPTIMIZATION: Cache for parcel geometry to avoid repeated API calls
const parcelGeometryCache = new Map();
const CACHE_EXPIRY = 1000 * 60 * 60; // 1 hour

class ArchitecturalPlansService {
  static GOVMAP_WFS_BASE = 'https://open.govmap.gov.il/geoserver/opendata/wfs';
  static IPLAN_API_BASE = 'https://ags.iplan.gov.il/arcgisiplan/rest/services/PlanningPublic/xplan_without_77_78/MapServer/1/query';
  static TEL_AVIV_GIS_BASE = 'https://gisn.tel-aviv.gov.il/arcgis/rest/services/WM/IView2WM/MapServer/identify';

  /**
   * Get real architectural plans for a given גוש/חלקה
   * @param {string} gush - The גוש number
   * @param {string} helka - The חלקה number
   * @param {Object} coordinates - Optional coordinates from GovMap {x, y}
   * @param {string} searchAddress - Optional original address text for city detection
   * @param {Function} onStatusUpdate - Optional callback for status updates
   * @returns {Promise<Array>} - Array of architectural plan objects
   */
  static async getRealArchitecturalPlans(
    gush,
    helka,
    coordinates = null,
    searchAddress = null,
    onStatusUpdate = null,
    onPartialUpdate = null,
    geometryHints = {}
  ) {
    try {
      const {
        locateParcelRingWm = null,
        govmapParcelObjectId = null,
        parcelCadastreCentroidItm = null,
      } = geometryHints;
      console.log('🏗️ Getting real architectural plans for:', { gush, helka, coordinates, searchAddress });

      // If we don't have a searchAddress but have gush/helka, try reverse lookup
      let addressForCityDetection = searchAddress;
      if (!searchAddress && gush && helka) {
        console.log('🔍 No address provided, attempting reverse lookup...');
        addressForCityDetection = await this.getAddressFromGushHelka(gush, helka);
        if (addressForCityDetection) {
          console.log('🔍 Reverse lookup successful:', addressForCityDetection);
        }
      }

      let allPlans = [];

      const forceParcelWfs =
        typeof window !== 'undefined' && window.localStorage?.getItem('urbanix_force_parcel_wfs') === '1';

      let parcelGeometry = null;

      if (!forceParcelWfs && locateParcelRingWm && locateParcelRingWm.length >= 3) {
        const ring = locateParcelRingWm.filter((p) => Array.isArray(p) && p.length >= 2);
        if (ring.length >= 3) {
          const wmCentroid = this.calculateCentroid(ring);
          parcelGeometry = {
            centroid: this.convertWebMercatorToITM(wmCentroid.x, wmCentroid.y),
            fullGeometry: { type: 'Polygon', coordinates: [ring] },
            properties: {},
            source: 'locate_wm',
          };
          console.log('⚡ Parcel geometry from locate polygon (WM→ITM centroid)', { gush, helka });
        }
      }

      if (
        !parcelGeometry &&
        !forceParcelWfs &&
        parcelCadastreCentroidItm &&
        typeof parcelCadastreCentroidItm.x === 'number' &&
        typeof parcelCadastreCentroidItm.y === 'number'
      ) {
        parcelGeometry = {
          centroid: parcelCadastreCentroidItm,
          fullGeometry: null,
          properties: {},
          source: 'entitiesByPoint_wkt',
        };
        console.log('⚡ Parcel centroid from govmap-layers parcel_ownership WKT', { gush, helka });
      }

      const needsNetworkParcelGeometry = forceParcelWfs || !coordinates;
      if (!parcelGeometry && needsNetworkParcelGeometry) {
        if (onStatusUpdate) onStatusUpdate('מביא גיאומטריית חלקה...');
        parcelGeometry = await this.getParcelGeometry(gush, helka, {
          govmapObjectId: govmapParcelObjectId,
        });
      } else if (!parcelGeometry && coordinates) {
        console.log('⚡ Skipping WFS/getShape (using address coordinates → ITM)', { gush, helka });
      }

      if (!parcelGeometry) {
        console.log('❌ Could not get parcel geometry from WFS/getShape/locate');
        // If we have coordinates from the initial search, convert them to ITM for iPLAN
        if (coordinates) {
          console.log('✅ Using coordinates from initial search as fallback');
          console.log('🔄 Converting Web Mercator coordinates to ITM for iPLAN...');
          // Coordinates from GovMap autocomplete are in Web Mercator (EPSG:3857)
          // But iPLAN needs ITM (EPSG:2039)
          const itmCoords = this.convertWebMercatorToITM(coordinates.x, coordinates.y);
          console.log('✅ Converted coordinates:', itmCoords);
        }
      } else {
        console.log('📐 Got parcel geometry:', parcelGeometry);
      }

      // Determine parcel coordinates for municipal checks and iPLAN
      // Priority: parcel geometry centroid (already in ITM) > converted coordinates
      let parcelCoords = null;
      if (parcelGeometry) {
        parcelCoords = parcelGeometry.centroid; // Already in ITM from WFS
        console.log('✅ Using parcel geometry centroid (ITM):', parcelCoords);
      } else if (coordinates) {
        // Convert Web Mercator to ITM for iPLAN queries
        console.log('🔄 Input coordinates (Web Mercator):', coordinates);
        parcelCoords = this.convertWebMercatorToITM(coordinates.x, coordinates.y);
        console.log('✅ Converted to ITM for iPLAN:', parcelCoords);
      } else {
        console.log('❌ No coordinates available at all!');
      }

      // Debug: compare address coords vs parcel centroid when both are available
      if (coordinates) {
        const addressItm = this.convertWebMercatorToITM(coordinates.x, coordinates.y);
        if (parcelGeometry && parcelGeometry.centroid) {
          const centroidItm = parcelGeometry.centroid;
          const distanceMeters = this.calculateDistanceMeters(addressItm, centroidItm);
          const parcelRing = parcelGeometry.fullGeometry?.coordinates?.[0]?.[0] || null;
          const addressInsideParcel = parcelRing ? this.isPointInsidePolygon(addressItm, parcelRing) : null;
          console.log('🧭 Parcel centroid vs address coords:', {
            addressWebMercator: coordinates,
            addressITM: addressItm,
            centroidITM: centroidItm,
            distanceMeters: Math.round(distanceMeters),
            addressInsideParcel
          });
        } else {
          console.log('🧭 Parcel centroid vs address coords:', {
            addressWebMercator: coordinates,
            addressITM: addressItm,
            centroidITM: null,
            distanceMeters: null,
            addressInsideParcel: null
          });
        }
      }
      
      // OPTIMIZATION: Start parallel fetches for all operations
      console.log('⚡ Starting parallel API fetches...');
      console.log('📊 Coordinates check:', {
        hasParcelGeometry: !!parcelGeometry,
        hasParcelCoords: !!parcelCoords,
        parcelCoordsValue: parcelCoords,
        hasInitialCoordinates: !!coordinates
      });
      const parallelFetches = [];
      
      // Fetch 1: iPLAN plans (needs coordinates - either from parcel geometry or initial search)
      if (parcelGeometry || parcelCoords) {
        console.log('✅ Will fetch iPLAN plans');
        if (onStatusUpdate) onStatusUpdate('מביא תוכניות בניה ממאגר ממשלתי...');
        
        // Create a parcelGeometry-like object if we only have coordinates
        const geomForQuery = parcelGeometry || { centroid: parcelCoords };
        console.log('📋 iPLAN query geometry:', geomForQuery);
        
        parallelFetches.push(
          this.getPlansForCoordinates(geomForQuery)
            .then(plans => {
              console.log('📋 iPLAN returned:', plans ? plans.length : 0, 'plans');
              return { type: 'iplan', data: plans };
            })
            .catch(error => {
              console.error('❌ iPLAN fetch error:', error);
              return { type: 'iplan', data: null };
            })
        );
      } else {
        console.log('⚠️ Skipping iPLAN fetch - no coordinates available');
      }
      
      // Fetch 2-5: All municipal data (independent of iPLAN)
      if (onStatusUpdate) onStatusUpdate('מביא נתונים עירוניים...');
      const municipalFetches = this.startMunicipalFetches(
        addressForCityDetection,
        coordinates,
        parcelCoords,
        gush,
        helka,
        onStatusUpdate
      );
      
      parallelFetches.push(...municipalFetches);
      
      // Wait for all parallel fetches to complete (emit partial updates as results arrive)
      if (parallelFetches.length > 0) {
        console.log(`⚡ Waiting for ${parallelFetches.length} parallel operations...`);
        let telAvivBuildingData = null;
        let rehovotBuildingData = null;
        let jerusalemBuildingData = null;
        let complotBuildingData = null;

        const emitPartialUpdate = () => {
          if (!onPartialUpdate) return;
          const buildingData = telAvivBuildingData || rehovotBuildingData || jerusalemBuildingData || complotBuildingData;
          onPartialUpdate({
            plans: [...allPlans],
            parcelGeometry,
            itmCoords: parcelCoords,
            buildingData,
            reverseLookupAddress: addressForCityDetection !== searchAddress ? addressForCityDetection : null,
            landUse: null
          });
        };

        const applyResult = (result) => {
          if (!result || !result.type) return;

          switch (result.type) {
            case 'iplan':
              if (result.data && result.data.length > 0) {
                const formattedPlans = this.formatPlansForUI(result.data);
                allPlans = [...allPlans, ...formattedPlans];
                console.log('📋 Found', formattedPlans.length, 'IPLAN architectural plans');
              }
              break;

            case 'tel_aviv':
              if (result.data) {
                const { permits, buildingData, outlinePlans } = result.data;
                if (permits && permits.length > 0) {
                  allPlans = [...allPlans, ...permits];
                  console.log('📋 Added', permits.length, 'Tel Aviv municipal permits');
                }
                if (outlinePlans && outlinePlans.length > 0) {
                  allPlans = [...allPlans, ...outlinePlans];
                  console.log('📋 Added', outlinePlans.length, 'Tel Aviv city building plans');
                }
                telAvivBuildingData = buildingData;
              }
              break;

            case 'rehovot':
              if (result.data) {
                const { permits, buildingData } = result.data;
                if (permits && permits.length > 0) {
                  allPlans = [...allPlans, ...permits];
                  console.log('📋 Added', permits.length, 'Rehovot municipal permits');
                }
                rehovotBuildingData = buildingData;
              }
              break;

            case 'jerusalem':
              if (result.data) {
                const { permits, buildingData } = result.data;
                if (permits && permits.length > 0) {
                  allPlans = [...allPlans, ...permits];
                  console.log('📋 Added', permits.length, 'Jerusalem municipal permits');
                }
                jerusalemBuildingData = buildingData;
              }
              break;

            case 'ramat_gan':
              if (result.data && result.data.plans && result.data.plans.length > 0) {
                allPlans = [...allPlans, ...result.data.plans];
                console.log('📋 Added', result.data.plans.length, 'Ramat Gan municipal plans');
              }
              break;

            case 'complot':
              if (result.data && result.data.plans && result.data.plans.length > 0) {
                allPlans = [...allPlans, ...result.data.plans];
                const municipalityName = result.municipality?.nameHe || 'Complot';
                console.log(`📋 Added ${result.data.plans.length} ${municipalityName} municipal plans`);
              }
              // Also add Complot permits if available
              if (result.data && result.data.permits && result.data.permits.length > 0) {
                allPlans = [...allPlans, ...result.data.permits];
                const municipalityName = result.municipality?.nameHe || 'Complot';
                console.log(`🏗️ Added ${result.data.permits.length} ${municipalityName} building permits`);
              }
              if (result.data && result.data.buildingData) {
                complotBuildingData = result.data.buildingData;
              }
              break;

            default:
              break;
          }
        };

        await Promise.all(
          parallelFetches.map((fetchPromise) =>
            fetchPromise
              .then((result) => {
                applyResult(result);
                emitPartialUpdate();
                return result;
              })
              .catch((error) => {
                console.error('❌ Parallel fetch error:', error);
                return null;
              })
          )
        );
        console.log('✅ All parallel fetches completed');

        // Determine best building data source (priority: Tel Aviv > Rehovot > Jerusalem)
        const buildingData = telAvivBuildingData || rehovotBuildingData || jerusalemBuildingData || complotBuildingData;

        console.log('📋 Total found:', allPlans.length, 'real architectural plans');
        
        // Get detailed land use if we have coordinates
        const coordsForLandUse = coordinates || parcelCoords;
        let detailedLandUse = null;
        if (coordsForLandUse) {
          try {
            if (onStatusUpdate) onStatusUpdate('מביא יעוד קרקע מפורט...');
            detailedLandUse = await LandUseService.getDetailedLandUse(coordsForLandUse);
            if (detailedLandUse) {
              console.log('🏛️ Got detailed land use:', detailedLandUse.landUse);
            }
          } catch (error) {
            console.error('⚠️ Land use fetch failed:', error);
          }
        }

        // Merge land use into building data
        let finalBuildingData = buildingData;
        if (detailedLandUse && detailedLandUse.landUse) {
          if (!finalBuildingData) finalBuildingData = {};
          finalBuildingData.landUse = detailedLandUse.landUse;
          finalBuildingData.landUseSource = detailedLandUse.source;
          finalBuildingData.landUseSourceLabel = detailedLandUse.sourceLabel;
          finalBuildingData.landUsePlan = detailedLandUse.plan;
          finalBuildingData.landUseConfidence = detailedLandUse.confidence;
        }

        return {
          plans: allPlans,
          parcelGeometry: parcelGeometry,
          itmCoords: parcelCoords,
          buildingData: finalBuildingData,
          reverseLookupAddress: addressForCityDetection !== searchAddress ? addressForCityDetection : null,
          landUse: detailedLandUse
        };
      } else {
        console.log('⚠️ No parallel fetches queued - using fallback logic');
        
        // Fallback: Try to get plans without coordinates if we have gush/helka
        if (!allPlans.length && gush && helka) {
          console.log('🏢 Trying municipal APIs as fallback without coordinates...');
          
          // Try Ramat Gan (works with just gush/helka)
          const ramatGanData = await this.getRamatGanMunicipalPlans(gush, helka);
          if (ramatGanData && ramatGanData.plans && ramatGanData.plans.length > 0) {
            allPlans = [...allPlans, ...ramatGanData.plans];
            console.log('📋 Added', ramatGanData.plans.length, 'Ramat Gan municipal plans (fallback)');
          }
          
          // Try Rehovot
          if (!allPlans.length) {
            const rehovotData = await this.getRehovotMunicipalData(gush, helka);
            if (rehovotData && rehovotData.permits && rehovotData.permits.length > 0) {
              allPlans = [...allPlans, ...rehovotData.permits];
              console.log('📋 Added', rehovotData.permits.length, 'Rehovot municipal permits (fallback)');
            }
          }
          
          // Try Jerusalem  
          if (!allPlans.length) {
            const jerusalemData = await this.getJerusalemMunicipalData(gush, helka);
            if (jerusalemData && jerusalemData.permits && jerusalemData.permits.length > 0) {
              allPlans = [...allPlans, ...jerusalemData.permits];
              console.log('📋 Added', jerusalemData.permits.length, 'Jerusalem municipal permits (fallback)');
            }
          }
        }

        console.log('📋 Total found:', allPlans.length, 'real architectural plans');

        return {
          plans: allPlans,
          parcelGeometry: parcelGeometry,
          itmCoords: parcelCoords,
          buildingData: null,
          reverseLookupAddress: addressForCityDetection !== searchAddress ? addressForCityDetection : null,
          landUse: null
        };
      }

    } catch (error) {
      console.error('❌ Error getting real architectural plans:', error);
      return null;
    }
  }

  static async tryGetParcelGeometryFromGetShape(objectId) {
    if (objectId == null || String(objectId).trim() === '') return null;
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/govmap-layers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ action: 'getShape', idType: 'parcel', id: String(objectId) }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return this.normalizeGovmapShapeToParcelGeometry(data);
    } catch (e) {
      console.warn('⚠️ GovMap getShape error:', e.message);
      return null;
    }
  }

  static normalizeGovmapShapeToParcelGeometry(shapeJson) {
    if (!shapeJson) return null;
    let ring = null;
    const g = shapeJson.geometry;
    if (g?.type === 'MultiPolygon' && Array.isArray(g.coordinates?.[0]?.[0])) {
      ring = g.coordinates[0][0];
    } else if (g?.type === 'Polygon' && Array.isArray(g.coordinates?.[0])) {
      ring = g.coordinates[0];
    } else {
      ring = GovMapService.extractLocateParcelRingWm(shapeJson);
    }
    if (!ring || ring.length < 3) return null;
    const x0 = ring[0][0];
    const itmLikely = x0 < 500000 && x0 > 5000;
    if (itmLikely) {
      const centroid = this.calculateCentroid(ring);
      return {
        centroid,
        fullGeometry: { type: 'Polygon', coordinates: [ring] },
        properties: {},
        source: 'govmap_getShape_itm',
      };
    }
    const wmCentroid = this.calculateCentroid(ring);
    return {
      centroid: this.convertWebMercatorToITM(wmCentroid.x, wmCentroid.y),
      fullGeometry: { type: 'Polygon', coordinates: [ring] },
      properties: {},
      source: 'govmap_getShape_wm',
    };
  }

  /**
   * Get parcel geometry: GovMap getShape (if object id), else open WFS.
   * OPTIMIZATION: Client cache 1h; server WFS cache in api/geocoding.
   */
  static async getParcelGeometry(gush, helka, options = {}) {
    const { govmapObjectId } = options;
    const cacheKey = `${gush}-${helka}`;

    // Check cache first
    if (parcelGeometryCache.has(cacheKey)) {
      const cached = parcelGeometryCache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_EXPIRY) {
        console.log('📦 Using cached parcel geometry for:', cacheKey);
        return cached.data;
      } else {
        console.log('⏰ Cache expired for:', cacheKey);
        parcelGeometryCache.delete(cacheKey);
      }
    }

    if (govmapObjectId != null && String(govmapObjectId).trim() !== '') {
      const fromShape = await this.tryGetParcelGeometryFromGetShape(govmapObjectId);
      if (fromShape) {
        parcelGeometryCache.set(cacheKey, { data: fromShape, timestamp: Date.now() });
        console.log('💾 Cached parcel geometry (getShape) for:', cacheKey);
        return fromShape;
      }
    }

    try {
      console.log('🌍 Fetching parcel geometry via WFS for:', { gush, helka });

      const response = await fetch(`${config.apiBaseUrl}/api/geocoding?action=parcel&gush=${gush}&helka=${helka}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('⚠️ Parcel geometry request failed:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('📐 Parcel geometry response:', data);

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        if (feature.geometry && feature.geometry.coordinates) {
          // Calculate centroid of the parcel for plan lookup
          const coordinates = feature.geometry.coordinates[0][0]; // Get first polygon ring
          const centroid = this.calculateCentroid(coordinates);
          const result = {
            centroid: centroid,
            fullGeometry: feature.geometry,
            properties: feature.properties
          };
          
          // Cache the result
          parcelGeometryCache.set(cacheKey, {
            data: result,
            timestamp: Date.now()
          });
          console.log('💾 Cached parcel geometry for:', cacheKey);
          
          return result;
        }
      }

      return null;
    } catch (error) {
      console.error('❌ Error fetching parcel geometry:', error);
      return null;
    }
  }

  /**
   * Calculate centroid of polygon coordinates
   */
  static calculateCentroid(coordinates) {
    let x = 0;
    let y = 0;
    const numPoints = coordinates.length;

    for (const [lng, lat] of coordinates) {
      x += lng;
      y += lat;
    }

    return {
      x: x / numPoints,
      y: y / numPoints
    };
  }

  static calculateDistanceMeters(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  static isPointInsidePolygon(point, polygon) {
    // Ray-casting algorithm for point-in-polygon
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      const intersects = (yi > point.y) !== (yj > point.y) &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  }

  /**
   * Convert Web Mercator (EPSG:3857) to ITM (EPSG:2039)
   * Used when WFS fails and we need to convert coordinates for iPLAN
   * 
   * Calibrated using known point: רות 14, רמת גן
   * Web Mercator (3874466.77, 3775064.31) → ITM (181741.96, 666310.60)
   */
  static convertWebMercatorToITM(wmX, wmY) {
    try {
      const [itmX, itmY] = proj4('EPSG:3857', 'EPSG:2039', [wmX, wmY]);
      console.log(`🌍 Converted Web Mercator [${wmX.toFixed(1)}, ${wmY.toFixed(1)}] → ITM [${itmX.toFixed(1)}, ${itmY.toFixed(1)}]`);
      return { x: itmX, y: itmY };
    } catch (error) {
      console.warn('⚠️ proj4 conversion failed, falling back to approximate conversion:', error.message);
      // Step 1: Convert Web Mercator to WGS84 lat/lon
      const lon = wmX * 180 / 20037508.34;
      const latRad = 2 * (Math.atan(Math.exp(wmY * Math.PI / 20037508.34)) - Math.PI / 4);
      const lat = latRad * 180 / Math.PI;

      // Step 2: Convert WGS84 to ITM using calibrated transformation
      const scaleX = 87930.0;
      const scaleY = 110946.0;
      const offsetX = -2878655.29;
      const offsetY = -2893890.79;

      const itmX = offsetX + scaleX * lon;
      const itmY = offsetY + scaleY * lat;

      console.log(`🌍 Converted Web Mercator [${wmX.toFixed(1)}, ${wmY.toFixed(1)}] → WGS84 [${lat.toFixed(6)}°, ${lon.toFixed(6)}°] → ITM [${itmX.toFixed(1)}, ${itmY.toFixed(1)}]`);

      return { x: itmX, y: itmY };
    }
  }

  /**
   * Get architectural plans for given coordinates from iplan.gov.il
   */
  static async getPlansForCoordinates(parcelGeometry) {
    try {
      console.log('📋 getPlansForCoordinates called with:', parcelGeometry);
      
      if (!parcelGeometry || !parcelGeometry.centroid) {
        console.error('❌ No centroid in parcelGeometry!');
        return null;
      }
      
      const { x, y } = parcelGeometry.centroid;
      
      console.log('📋 Fetching plans via Vercel Function for coordinates:', { x, y });

      const response = await fetch(`${config.apiBaseUrl}/api/plans?x=${x}&y=${y}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('⚠️ Plans request failed:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('📋 Plans response:', data);

      if (data.features && data.features.length > 0) {
        return data.features.map(feature => feature.attributes);
      }

      return [];
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      return null;
    }
  }

  /**
   * Query Tel Aviv DB-indexed relevance endpoint (applies + nearby).
   * Input coordinates are expected in Web Mercator from GovMap.
   */
  static async getTaRelevantForCoordinates(coordinates, options = {}) {
    try {
      if (!coordinates || typeof coordinates.x !== 'number' || typeof coordinates.y !== 'number') {
        return null;
      }
      const params = new URLSearchParams({
        action: 'ta-relevant',
        x: String(coordinates.x),
        y: String(coordinates.y),
        sr: '3857',
        radiusMeters: String(options.radiusMeters || 300),
        maxResults: String(options.maxResults || 150),
      });
      if (options.statusType) params.set('statusType', String(options.statusType));
      if (options.entity) params.set('entity', String(options.entity));

      const response = await fetch(`${config.apiBaseUrl}/api/plans?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn('⚠️ ta-relevant request failed:', error?.message || error);
      return null;
    }
  }

  /**
   * Decode authority code to Hebrew text
   */
  static decodeAuthority(code) {
    const authorityMap = {
      1: 'מקומית',
      2: 'ארצית',
      3: 'מחוזית'
    };
    return authorityMap[code] || null;
  }

  /**
   * Format raw plan data for our UI
   */
  static formatPlansForUI(rawPlans) {
    return rawPlans.map((plan, index) => ({
      id: `real_${index + 1}`,
      title: plan.pl_name || 'תוכנית ללא שם',
      status: this.translateStatus(plan.internet_short_status || plan.station_desc),
      statusType: this.getStatusType(plan.internet_short_status || plan.station_desc),
      submissionDate: this.formatDate(plan.pl_date_advertise),
      approvalDate: this.formatDate(plan.pl_date_8),
      planNumber: plan.pl_number,
      planUrl: plan.pl_url,
      architect: 'לא צוין', // Not provided by API
      planType: this.getPlanType(plan.pl_name),
      area: plan.pl_area_dunam ? `${plan.pl_area_dunam.toLocaleString('he-IL')} דונם` : null,
      landUse: plan.pl_landuse_string,
      county: plan.plan_county_name,
      objectives: plan.pl_objectives || null, // מטרות התוכנית
      planSubtype: plan.entity_subtype_desc || null, // תת-סוג תוכנית  
      permitType: plan.plan_charactor_name || null, // סוג היתר שניתן להוציא
      approvingAuthority: this.decodeAuthority(plan.pl_by_auth_of), // הסמכות המאשרת (decoded from numeric)
      location: plan.district_name || plan.jurstiction_area_name || null, // מיקום
      canIssuePermits: plan.plan_charactor_name ? plan.plan_charactor_name.includes('תכנית שמכוחה ניתן להוציא היתרים') : false, // ניתן להוציא היתרים
      isRealData: true
    }));
  }

  /**
   * Translate plan status to Hebrew
   */
  static translateStatus(status) {
    if (!status) return 'לא ידוע';
    
    const statusMap = {
      'התכנית אושרה': 'מאושר',
      'אישור': 'מאושר', 
      'בבדיקה': 'בבדיקה',
      'לא אושר': 'לא אושר',
      'הוגש': 'הוגש',
      'תלוי': 'ממתין',
      'מבוטל': 'מבוטל'
    };

    return statusMap[status] || status;
  }

  /**
   * Get status type for styling
   */
  static getStatusType(status) {
    if (!status) return 'unknown';
    
    if (status.includes('אושר') || status === 'אישור') return 'approved';
    if (status.includes('בבדיקה') || status.includes('ממתין')) return 'pending';
    if (status.includes('מבוטל') || status.includes('לא אושר')) return 'rejected';
    
    return 'pending';
  }

  /**
   * Extract plan type from plan name
   */
  static getPlanType(planName) {
    if (!planName) return 'תוכנית כללית';
    
    if (planName.includes('מפורטת')) return 'תוכנית מפורטת';
    if (planName.includes('בנין עיר')) return 'תוכנית בנין עיר';
    if (planName.includes('מתאר')) return 'תוכנית מתאר';
    if (planName.includes('חלוקה')) return 'תוכנית חלוקה';
    if (planName.includes('איחוד')) return 'תוכנית איחוד וחלוקה';
    if (planName.includes('שימור')) return 'תוכנית שימור';
    
    return 'תוכנית כללית';
  }

  /**
   * Format timestamp to Hebrew date
   */
  static formatDate(timestamp) {
    if (!timestamp) return 'לא ידוע';
    
    try {
      let date;
      
      // Handle DD/MM/YYYY HH:MM:SS format (Tel Aviv municipal data)
      if (typeof timestamp === 'string' && timestamp.includes('/')) {
        const parts = timestamp.split(' ')[0].split('/'); // Get date part only
        if (parts.length === 3) {
          // Convert DD/MM/YYYY to MM/DD/YYYY for Date constructor
          const [day, month, year] = parts;
          date = new Date(`${month}/${day}/${year}`);
        } else {
          date = new Date(timestamp);
        }
      } else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'לא ידוע';
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'לא ידוע';
    }
  }

  // =============================================================================
  // GOVMAP REVERSE LOOKUP UTILITIES  
  // =============================================================================

  /**
   * Official GovMap reverse lookup: gush/helka → parcel ID → full address
   */
  static async getAddressFromGushHelka(gush, helka) {
    try {
      console.log('🔍 Official reverse lookup for gush/helka:', gush, helka);
      
      // Step 1: Get parcel ID from gush/helka using parcel filter
      const parcelSearchParams = {
        searchText: `${gush} ${helka}`,
        language: 'he',
        filterType: 'parcel',
        isAccurate: false,
        maxResults: 10
      };

      console.log('🔍 Step 1: Searching for parcel ID via Vercel Function...');
      const parcelResponse = await fetch(`${config.apiBaseUrl}/api/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parcelSearchParams)
      });

      if (!parcelResponse.ok) {
        throw new Error(`Parcel search failed: ${parcelResponse.status}`);
      }

      const parcelData = await parcelResponse.json();
      if (!parcelData.results || parcelData.results.length === 0) {
        throw new Error('No parcel found for gush/helka');
      }

      const normalizeParcelText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
      const expectedParcelText = normalizeParcelText(`גוש ${gush} חלקה ${helka}`);
      const exactParcel = parcelData.results.find(result => (
        result.id?.startsWith('parcel|') &&
        normalizeParcelText(result.text) === expectedParcelText
      ));

      if (!exactParcel) {
        console.warn('🔍 Parcel search returned no exact gush/helka match:', {
          expected: expectedParcelText,
          results: parcelData.results.map(result => result.text),
        });
        throw new Error('No exact parcel found for gush/helka');
      }

      // Extract parcel ID only after confirming the search result is an exact match.
      const firstParcel = exactParcel;
      const parcelIdMatch = firstParcel.id.match(/\|(\d+)$/);
      if (!parcelIdMatch) {
        throw new Error('Could not extract parcel ID from result');
      }
      
      const parcelId = parcelIdMatch[1];
      console.log('🔍 Step 1 success: Found parcel ID', parcelId, 'for', firstParcel.text);

      // Step 2: Get full address from parcel ID
      console.log('🔍 Step 2: Getting address from parcel ID via Vercel Function...');
      const addressResponse = await fetch(`${config.apiBaseUrl}/api/geocoding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'reverse', parcelId })
      });

      if (!addressResponse.ok) {
        throw new Error(`Address lookup failed: ${addressResponse.status}`);
      }

      const addressData = await addressResponse.json();
      if (!addressData || addressData.length === 0) {
        throw new Error('No address data found for parcel');
      }

      // Extract the best address (usually first one)
      const addressFeature = addressData[0];
      const props = addressFeature.properties;
      
      // Construct full address
      const streetName = props.str_name || '';
      const houseNum = props.house_num || '';
      const cityName = props.setl_name || props.locality_name || '';
      
      const fullAddress = `${streetName}${houseNum ? ' ' + houseNum : ''}${cityName ? ', ' + cityName : ''}`;
      console.log('🔍 Step 2 success: Found address:', fullAddress);
      
      return fullAddress;
      
    } catch (error) {
      console.error('🔍 Official reverse lookup failed:', error);
      return null;
    }
  }

  // =============================================================================
  // CITY DETECTION UTILITIES
  // =============================================================================

  /**
   * Detect if address is in Tel Aviv based on text content
   */
  static isAddressInTelAviv(address) {
    if (!address) return false;
    const addressLower = address.toLowerCase();
    const telAvivKeywords = ['תל אביב', 'תל-אביב', 'תל אביב-יפו', 'tel aviv', 'telaviv'];
    console.log('🏛️ Checking if address is in Tel Aviv:', address, '→', telAvivKeywords.some(keyword => addressLower.includes(keyword)));
    return telAvivKeywords.some(keyword => addressLower.includes(keyword));
  }

  /**
   * Detect if address is in Rehovot based on text content
   */
  static isAddressInRehovot(address) {
    if (!address) return false;
    const addressLower = address.toLowerCase();
    const rehovotKeywords = ['רחובות', 'rehovot'];
    console.log('🏢 Checking if address is in Rehovot:', address, '→', rehovotKeywords.some(keyword => addressLower.includes(keyword)));
    return rehovotKeywords.some(keyword => addressLower.includes(keyword));
  }

  /**
   * Detect if address is in Jerusalem based on text content
   */
  static isAddressInJerusalem(address) {
    if (!address) return false;
    const addressLower = address.toLowerCase();
    const jerusalemKeywords = ['ירושלים', 'ירושליים', 'jerusalem'];
    console.log('🕌 Checking if address is in Jerusalem:', address, '→', jerusalemKeywords.some(keyword => addressLower.includes(keyword)));
    return jerusalemKeywords.some(keyword => addressLower.includes(keyword));
  }

  /**
   * Detect if address is in Ramat Gan based on text content
   * Note: Ramat Gan municipal site uses client-side rendering without accessible APIs
   * We provide a link to their site but don't fetch plans programmatically
   */
  static isAddressInRamatGan(address) {
    if (!address) return false;
    const addressLower = address.toLowerCase();
    const ramatGanKeywords = ['רמת גן', 'רמת-גן', 'ramat gan', 'ramatgan'];
    return ramatGanKeywords.some(keyword => addressLower.includes(keyword));
  }

  /**
   * Check if coordinates are within Tel Aviv boundaries (DEPRECATED - use text-based detection)
   */
  static isInTelAviv(x, y) {
    // Tel Aviv PRECISE bounds in Web Mercator (EPSG:3857)
    // Much more restrictive boundaries to prevent data leaks to other cities
    const telAvivBounds = {
      minX: 3868000, // Western boundary (more restrictive)
      maxX: 3872000, // Eastern boundary (more restrictive)
      minY: 3772000, // Southern boundary (more restrictive)
      maxY: 3776000  // Northern boundary (more restrictive)
    };

    const isInBounds = (
      x >= telAvivBounds.minX && x <= telAvivBounds.maxX &&
      y >= telAvivBounds.minY && y <= telAvivBounds.maxY
    );
    
    // Log coordinate check for debugging
    console.log(`🏛️ Tel Aviv coordinate check: (${x}, ${y}) → ${isInBounds ? 'IN' : 'OUT'} of bounds`);
    
    return isInBounds;
  }

  /**
   * Get Tel Aviv municipal planning data using coordinates
   */
  static async getTelAvivMunicipalData(x, y, gush = null, helka = null) {
    try {
      console.log('🏛️ Getting Tel Aviv municipal data via Vercel Function for coordinates:', { x, y, gush, helka });

      const params = new URLSearchParams({
        city: 'tel-aviv',
        x: x.toString(),
        y: y.toString()
      });
      
      if (gush) params.append('gush', gush);
      if (helka) params.append('helka', helka);

      const response = await fetch(`${config.apiBaseUrl}/api/municipal-1?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('⚠️ Tel Aviv GIS API failed with status:', response.status);
        return null;
      }

      const result = await response.json();
      console.log('🏛️ Tel Aviv GIS response:', result);

      // Extract the actual data from the wrapper
      return this.formatTelAvivPlansForUI(result.data);

    } catch (error) {
      console.error('❌ Error calling Tel Aviv GIS API:', error);
      return null;
    }
  }

  /**
   * Format Tel Aviv GIS response for UI display
   */
  static formatTelAvivPlansForUI(gisResponse) {
    if (!gisResponse || !gisResponse.results) {
      return { permits: [], buildingData: null, outlinePlans: [] };
    }

    const permits = [];
    const outlinePlans = []; // Layer 528 - city building plans
    let buildingData = null;
    let landUse = null;
    let tama38Status = null;
    
    // Process each layer in the response
    gisResponse.results.forEach((result, index) => {
      const layerId = typeof result.layerId === 'string' ? parseInt(result.layerId, 10) : result.layerId;
      if (result.attributes && layerId) {
        const attrs = result.attributes;
        
        // Handle building permits layer (772) - these become cards
        if (layerId === 772) {
          console.log('🏗️ Processing permit with keys:', Object.keys(attrs));
          console.log('🏗️ Processing permit:', {
            request_num: attrs.request_num || attrs['מספר בקשה'],
            permission_num: attrs.permission_num || attrs['מספר היתר'],
            expiry_date: attrs.expiry_date || attrs['תאריך תפוגה'],
            maslul_rishuy: attrs.maslul_rishuy || attrs['מסלול רישוי'],
            sug_bakasha: attrs.sug_bakasha || attrs['סוג בקשה']
          });
          
          const planData = {
            id: `tel-aviv-permit-${attrs.request_num || attrs['מספר בקשה'] || index}`,
            title: this.formatTelAvivPermitTitle(attrs),
            status: attrs.building_stage || attrs['שלב בנייה'] || 'בתהליך',
            statusType: this.getTelAvivStatusType(attrs.building_stage || attrs['שלב בנייה']),
            submissionDate: this.formatDate(attrs.open_request || attrs['תאריך פתיחה']),
            approvalDate: (attrs.permission_date || attrs['תאריך היתר']) ? this.formatDate(attrs.permission_date || attrs['תאריך היתר']) : null,
            expiryDate: (attrs.expiry_date || attrs['תאריך תפוגה']) ? this.formatDate(attrs.expiry_date || attrs['תאריך תפוגה']) : null,
            planNumber: (attrs.request_num || attrs['מספר בקשה'])?.toString() || null,
            permitNumber: (attrs.permission_num || attrs['מספר היתר'])?.toString() || null,
            requestType: attrs.sug_bakasha || attrs['סוג בקשה'] || 'היתר בנייה',
            planType: attrs.sug_bakasha || attrs['סוג בקשה'] || 'היתר בנייה',
            licensingTrack: attrs.maslul_rishuy || attrs['מסלול רישוי'] || null,
            area: null, // Not available in permits layer
            county: 'תל אביב-יפו',
            address: attrs.addresses || attrs['כתובת'] || null,
            buildingNumber: attrs.building_num || attrs['מספר בניין'] || null,
            content: attrs.tochen_bakasha || attrs['תוכן בקשה'] || null,
            finished: attrs.finished || attrs['סיום'] || null,
            occupation: attrs.occupation || attrs['אכלוס'] || null,
            isRealData: true,
            isTelAvivMunicipal: true,
            isTelAvivPermit: true,
            telAvivRequestId: attrs.request_num || attrs['מספר בקשה'],
            dataSource: 'Tel Aviv Municipality - Building Permits',
            planUrl: (attrs.request_num || attrs['מספר בקשה']) ? 
              `https://www5.tel-aviv.gov.il/RishuyBniyaToshav/LicensingRequestPages/LicensingRequestFromGIS.aspx?licensingRequestId=${attrs.request_num || attrs['מספר בקשה']}` 
              : null
          };
          
          console.log('🏗️ Formatted permit data:', {
            id: planData.id,
            expiryDate: planData.expiryDate,
            licensingTrack: planData.licensingTrack
          });

          permits.push(planData);
        }
        
        // Handle city building plans layer (528, 527) and Klal Ironiyot (683)
        else if (layerId === 528 || layerId === 527 || layerId === 683) {
          console.log(`🏛️ LAYER ${result.layerId} - Attribute keys:`, Object.keys(attrs));
          console.log(`🏛️ LAYER ${result.layerId} - Processing city plan:`, attrs.taba || attrs['טאבה'], '-', attrs.shem_taba || attrs['שם טאבה']);
          
          // Determine plan type based on layerName or layerId
          let planType = 'תוכנית בניין עיר';
          
          // Explicitly set Klal Ironiyot for layer 683
          if (layerId === 683) {
             planType = 'תוכנית כלל עירונית';
             console.log(`✅ Identified Klal Ironiyot (Layer 683): ${attrs.shem_taba || attrs['שם טאבה']}`);
          } else {
             // Logic for 528/527
             // Broaden the check for title keywords that strongly imply city-wide scope
             if (result.layerName && (result.layerName.includes('כלל') || result.layerName.toLowerCase().includes('klal'))) {
                planType = 'תוכנית כלל עירונית';
             } else if ((attrs.shem_taba || attrs['שם טאבה']) && (
                (attrs.shem_taba || attrs['שם טאבה']).includes('כלל') || 
                (attrs.shem_taba || attrs['שם טאבה']).includes('מתאר מקומית') ||
                (attrs.shem_taba || attrs['שם טאבה']).includes('תכנית מתאר') ||
                (attrs.shem_taba || attrs['שם טאבה']).includes('תכנית אב')
             )) {
                planType = 'תוכנית כלל עירונית'; 
             } else if (result.layerName && (result.layerName.includes('בניין') || result.layerName.toLowerCase().includes('binyan'))) {
                planType = 'תוכנית בניין עיר';
             }
          }
          
          // Determine if permits can be issued - מפורטת plans allow permits
          const planSubtype = attrs.t_sivug || attrs['סיווג'] || '';
          const canIssuePermits = planSubtype.includes('מפורטת');
          
          const planData = {
            id: `tel-aviv-outline-${attrs.id_taba || attrs['מזהה טאבה'] || attrs.taba || attrs['טאבה'] || index}`,
            title: attrs.shem_taba || attrs['שם טאבה'] || attrs.taba || attrs['טאבה'] || planType,
            status: attrs.t_status || attrs['סטטוס'] || 'לא ידוע',
            statusType: this.getTelAvivStatusType(attrs.t_status_klali || attrs['סטטוס כללי'] || attrs.t_status || attrs['סטטוס']),
            submissionDate: (attrs.tr_hafkada || attrs['תאריך הפקדה']) ? this.formatDate(attrs.tr_hafkada || attrs['תאריך הפקדה']) : null,
            approvalDate: (attrs.tr_matan_tokef || attrs['תאריך מתן תוקף']) ? this.formatDate(attrs.tr_matan_tokef || attrs['תאריך מתן תוקף']) : null,
            planNumber: (attrs.taba || attrs['טאבה'])?.toString() || null,
            planType: planType,
            planSubtype: planSubtype || null, // סוג תת-התוכנית
            location: attrs.t_hekef || attrs['היקף'] || null, // מיקום
            approvingAuthority: attrs.maslul || attrs['מסלול'] || null, // הסמכות המאשרת
            area: null,
            county: 'תל אביב-יפו',
            canIssuePermits: canIssuePermits, // ניתן להוציא היתרים if contains "מפורטת"
            isRealData: true,
            isTelAvivOutlinePlan: true,
            dataSource: 'תל אביב-יפו',
            planUrl: attrs.url_iplan || attrs.url_mavat || attrs.url_documents || attrs['קישור מסמכים'] || null,
            layerName: result.layerName || null
          };
          
          // Deduplication Logic
          const planNum = planData.planNumber;
          const existingIndex = outlinePlans.findIndex(p => p.planNumber === planNum);

          if (existingIndex !== -1) {
            // Duplicate found. 
            // If the current one is explicitly "Klal" (Layer 683) and the existing one isn't, replace it.
            if (layerId === 683 && outlinePlans[existingIndex].planType !== 'תוכנית כלל עירונית') {
              console.log(`♻️ Replacing duplicate plan ${planNum} with Layer 683 (Klal) version.`);
              outlinePlans[existingIndex] = planData;
            } else {
              console.log(`♻️ Skipping duplicate plan ${planNum} (already exists).`);
            }
          } else {
            outlinePlans.push(planData);
          }
        }
        
        // Handle buildings layer (513) - this becomes property info
        // Multiple 513 results can appear; keep the one with the latest שנת בניה
        else if (layerId === 513) {
          // Calculate height: Priority 1: gova_simplex_2019, Priority 2: max_height - min_height
          let calculatedHeight = null;
          if (attrs.gova_simplex_2019) {
            calculatedHeight = parseFloat(attrs.gova_simplex_2019);
          } else if (attrs.max_height && attrs.min_height) {
            calculatedHeight = parseFloat(attrs.max_height) - parseFloat(attrs.min_height);
          }

          const yearKey = Object.keys(attrs).find((key) => {
            const normalizedKey = key.replace(/\s+/g, ' ').trim();
            return (
              normalizedKey === 'year' ||
              normalizedKey === 'שנת בניה' ||
              normalizedKey === 'שנת בנייה' ||
              normalizedKey.includes('שנת בניה') ||
              normalizedKey.includes('שנת בנייה')
            );
          });
          const rawYear = attrs.year || (yearKey ? attrs[yearKey] : null);
          const parsedYear = typeof rawYear === 'string' ? rawYear.trim() : rawYear;
          console.log('🏛️ LAYER 513 - Attribute keys:', Object.keys(attrs));
          console.log('🏛️ LAYER 513 - Year extraction:', { yearKey, rawYear, parsedYear });

          // Floor / height sanity check:
          // If both are present and floors is implausibly low for the given height,
          // override floors from height and flag it.
          const rawFloors = attrs.ms_komot || attrs['מספר קומות'] || null;
          const rawFloorsNum = rawFloors ? parseInt(rawFloors) : NaN;
          let floorsOverridden = false;
          let floorsOverrideReason = null;
          let correctedFloors = null;
          if (!isNaN(rawFloorsNum) && rawFloorsNum > 0 && calculatedHeight && calculatedHeight > 0) {
            const impliedFloorHeight = calculatedHeight / rawFloorsNum;
            if (impliedFloorHeight > 6) {
              const estimated = Math.floor(calculatedHeight / 3.6);
              if (estimated >= 1 && estimated !== rawFloorsNum) {
                console.log(`⚠️ LAYER 513 - Floor/height mismatch: ${rawFloorsNum} floors but ${calculatedHeight}m height (${impliedFloorHeight.toFixed(1)}m/floor). System will use ${estimated} floors.`);
                floorsOverridden = true;
                correctedFloors = estimated;
                floorsOverrideReason = `נתון זה אינו תואם לגובה המבנה (${calculatedHeight.toFixed(1)} מ׳). המערכת משתמשת ב-${estimated} קומות (הערכה) לצורך החישובים.`;
              }
            }
          }

          const candidate = {
            buildingNumber: attrs['זיהוי מבנה/אתר'] || null,
            buildingType: attrs.t_sug_mivne || attrs['סוג מבנה'] || null,
            floors: rawFloors,
            height: calculatedHeight ? calculatedHeight.toFixed(1) : null,
            minHeight: attrs.min_height ? parseFloat(attrs.min_height).toFixed(1) : null,
            area: attrs.Shape_Area ? Math.round(parseFloat(attrs.Shape_Area)) : null,
            year: parsedYear || null,
            buildingName: attrs['שם מבנה'] || null,
            hasColumns: attrs.t_amudim || attrs['עמודים'] || null,
            columnsCode: attrs.k_amudim || null,
            asbestLevel: attrs.t_asbest_level || null,
            floorsOverridden,
            floorsOverrideReason,
            correctedFloors,
            dataSource: 'Tel Aviv Municipality - Buildings Registry'
          };

          if (!buildingData) {
            buildingData = candidate;
          } else {
            const existingYear = parseInt(buildingData.year) || 0;
            const candidateYear = parseInt(candidate.year) || 0;
            if (candidateYear > existingYear) {
              console.log(`🏛️ LAYER 513 - Duplicate detected. Replacing year ${existingYear} with newer ${candidateYear}.`);
              buildingData = candidate;
            } else {
              console.log(`🏛️ LAYER 513 - Duplicate detected. Keeping existing year ${existingYear} over ${candidateYear}.`);
            }
          }
        }
        
        // Handle land use layer (837) - ייעוד הקרקע
        else if (layerId === 837) {
          landUse = result.value || null;
          console.log('🏛️ LAYER 837 - Raw result:', result);
          console.log('🏛️ LAYER 837 - Extracted land use:', landUse);
        }
        
        // Handle TAMA 38 zones layer (801)
        else if (layerId === 801) {
          console.log('🏛️ LAYER 801 - Raw result:', result);
          console.log('🏛️ LAYER 801 - Attributes:', attrs);
          tama38Status = {
            layerName: result.layerName || null,
            zoneDescription: attrs.zone_desc || attrs['תיאור אזור'] || null,
            documentLink: attrs.document || attrs['מסמך'] || attrs.url_document || attrs['קישור מסמך'] || attrs.url_documents || attrs['קישור מסמכים'] || null
          };
          console.log('🏛️ LAYER 801 - Extracted TAMA 38 zone:', tama38Status);
        }
        
        // Handle conservation areas layer (687) - אזורי אחריות שימור מבנים
        else if (layerId === 687) {
          console.log('🏛️ LAYER 687 - Conservation area detected:', {
            layerName: result.layerName,
            responsibilityName: attrs.shem_achray || attrs['שם אחראי'],
            areaName: attrs.shem_ezor || attrs['שם אזור'],
            attributes: attrs
          });
          
          // Store conservation area info in building data
          if (!buildingData) buildingData = {};
          buildingData.conservationArea = {
            inConservationZone: true,
            responsibilityName: attrs.shem_achray || attrs['שם אחראי'] || null,
            areaName: attrs.shem_ezor || attrs['שם אזור'] || null,
            layerName: result.layerName || 'אזורי אחריות שימור מבנים'
          };
        }
        
        // Handle conservation buildings/sites layer (682) - מבנים ואתרים לשימור
        else if (layerId === 682) {
          console.log('🏛️ LAYER 682 - Conservation site detected:', {
            layerName: result.layerName,
            designation: attrs.t_hatraa || attrs['t_hatraa'],
            addresses: attrs.ktovot || attrs['כתובות'],
            planNumber: attrs.st_taba || attrs['סטטוס תב"ע'] || attrs['סטטוס תב״ע'],
            attributes: attrs
          });
          
          if (!buildingData) buildingData = {};
          buildingData.conservationSite = {
            isConservationSite: true,
            designation: attrs.t_hatraa || attrs['t_hatraa'] || null,
            addresses: attrs.ktovot || attrs['כתובות'] || null,
            planNumber: attrs.st_taba || attrs['סטטוס תב"ע'] || attrs['סטטוס תב״ע'] || null,
            lastUpdated: attrs.tr_hatraot || attrs['tr_hatraot'] || null,
            layerName: result.layerName || 'מבנים ואתרים לשימור'
          };
        }
      }
    });

    // Merge land use and TAMA 38 status into building data if available
    if (buildingData) {
      if (landUse) {
        buildingData.landUse = landUse;
      }
      if (tama38Status) {
        buildingData.tama38Zone = tama38Status;
      }
    } else if (landUse || tama38Status) {
      // If we only have land use/TAMA 38 but no building data, create minimal building data
      buildingData = {
        dataSource: 'Tel Aviv Municipality - Planning Registry'
      };
      if (landUse) {
        buildingData.landUse = landUse;
      }
      if (tama38Status) {
        buildingData.tama38Zone = tama38Status;
      }
    }

    console.log('🏛️ Formatted', permits.length, 'Tel Aviv municipal permits and building data:', buildingData ? 'found' : 'not found');
    console.log('🏛️ Formatted', outlinePlans.length, 'Tel Aviv city building plans (layer 528)');
    console.log('🏛️ DEBUG - Final buildingData:', buildingData);
    if (buildingData && buildingData.landUse) {
      console.log('🏛️ Land use (ייעוד הקרקע):', buildingData.landUse);
    }
    if (buildingData && buildingData.tama38Zone) {
      console.log('🏛️ TAMA 38 zone:', buildingData.tama38Zone);
    }
    return { permits, buildingData, outlinePlans };
  }

  /**
   * Format Tel Aviv permit title
   */
  static formatTelAvivPermitTitle(attrs) {
    if (attrs.koteret || attrs['כותרת']) return attrs.koteret || attrs['כותרת'];
    if (attrs.sug_bakasha || attrs['סוג בקשה']) return `היתר ${attrs.sug_bakasha || attrs['סוג בקשה']}`;
    if (attrs.request_num || attrs['מספר בקשה']) return `בקשה ${attrs.request_num || attrs['מספר בקשה']}`;
    return 'היתר בנייה תל אביב';
  }

  /**
   * Get Tel Aviv specific status type for styling
   */
  static getTelAvivStatusType(buildingStage) {
    if (!buildingStage) return 'pending';
    
    const stage = buildingStage.toLowerCase();
    if (stage.includes('תעודת גמר') || stage.includes('גמר')) return 'approved';
    if (stage.includes('בתהליך') || stage.includes('בבניה')) return 'pending';
    if (stage.includes('מבוטל') || stage.includes('נדחה')) return 'rejected';
    
    return 'approved'; // Default for existing buildings
  }

  // =============================================================================
  // REHOVOT MUNICIPALITY INTEGRATION
  // =============================================================================

  static REHOVOT_API_BASE = 'https://handasi.complot.co.il/magicscripts/mgrqispi.dll';
  static JERUSALEM_API_BASE = 'https://jerbasicserviceapi.jerusalem.muni.il/api/Db/ExecuteGetJSON';

  /**
   * Check if coordinates are within Rehovot city boundaries
   */
  static isInRehovot(x, y) {
    // Rehovot approximate bounds in Web Mercator (EPSG:3857)  
    // Updated based on actual coordinates from GovMap API
    const rehovotBounds = {
      minX: 3870000, // Western boundary
      maxX: 3880000, // Eastern boundary  
      minY: 3745000, // Southern boundary
      maxY: 3755000  // Northern boundary
    };

    console.log('🏢 Checking Rehovot boundaries for coordinates:', { x, y });
    const isInBounds = (
      x >= rehovotBounds.minX && x <= rehovotBounds.maxX &&
      y >= rehovotBounds.minY && y <= rehovotBounds.maxY
    );
    console.log('🏢 Is in Rehovot:', isInBounds);
    return isInBounds;
  }

  /**
   * Check if coordinates are within Jerusalem city boundaries
   */
  static isInJerusalem(x, y) {
    // Jerusalem approximate bounds in Web Mercator (EPSG:3857)
    const jerusalemBounds = {
      minX: 3885000, // Western boundary
      maxX: 3905000, // Eastern boundary  
      minY: 3750000, // Southern boundary
      maxY: 3770000  // Northern boundary
    };

    console.log('🕌 Checking Jerusalem boundaries for coordinates:', { x, y });
    const isInBounds = (
      x >= jerusalemBounds.minX && x <= jerusalemBounds.maxX &&
      y >= jerusalemBounds.minY && y <= jerusalemBounds.maxY
    );
    console.log('🕌 Is in Jerusalem:', isInBounds);
    return isInBounds;
  }

  /**
   * Get Rehovot municipal building permit data using gush/helka
   */
  static async getRehovotMunicipalData(gush, helka) {
    try {
      console.log('🏢 ===== CALLING REHOVOT MUNICIPAL API VIA VERCEL FUNCTION =====');
      console.log('🏢 Getting Rehovot municipal data for:', { gush, helka });

      // Proxy through our backend — the browser can't reach handasi.complot.co.il
      // directly (no CORS + Israeli-IP gating). Server-side it works.
      const directUrl = `${config.apiBaseUrl}/api/municipal-1?action=complot-raw&prgname=GetBakashotByGush&siteid=22&g=${gush}&h=${helka}`;

      const response = await fetch(directUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        }
      });

      if (!response.ok) {
        throw new Error(`Rehovot API error: ${response.status}`);
      }

      const htmlText = await response.text();
      console.log('🏢 Rehovot API response received, parsing HTML...');

      return await this.formatRehovotPlansForUI(htmlText);

    } catch (error) {
      console.error('🏢 Rehovot municipal data error:', error);
      return { permits: [], buildingData: null };
    }
  }

  /**
   * Parse Rehovot HTML response and format for UI
   */
  static async formatRehovotPlansForUI(htmlText) {
    try {
      console.log('🏢 Parsing Rehovot HTML response, length:', htmlText.length);
      // Extract permit data from HTML table rows
      const permits = [];
      const rows = htmlText.match(/<tr>[\s\S]*?<\/tr>/g) || [];
      console.log('🏢 Found table rows:', rows.length);

      // Pre-fetch each request's details CONCURRENTLY (was a sequential fetch
      // inside the loop — one round-trip per permit, back-to-back). Builds
      // requestNum -> {requestType, housingUnits}; same data, just parallel.
      const detailByRequest = new Map();
      await Promise.all(rows.map(async (row) => {
        const rMatch = row.match(/getRequest\(([^)]+)\)/);
        const rnMatch = row.match(/>(\d+)</);
        if (!rMatch || !rnMatch) return;
        const requestNum = rnMatch[1];
        try {
          const detailUrl = `${config.apiBaseUrl}/api/municipal-1?action=complot-raw&prgname=GetBakashaFile&siteid=22&b=${requestNum}`;
          const detailResponse = await fetch(detailUrl, {
            method: 'GET',
            headers: { 'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8' },
          });
          if (!detailResponse.ok) return;
          const detailHtml = await detailResponse.text();
          let requestType = '';
          let housingUnits = '';
          const requestTypeMatch = detailHtml.match(/<td class="title"[^>]*>סוג הבקשה<\/td>\s*<td[^>]*>([^<]+)<\/td>/);
          if (requestTypeMatch) requestType = requestTypeMatch[1].trim();
          const housingUnitsMatch = detailHtml.match(/<td class="title"[^>]*>סך מספר יחידות דיור המבוקשות<\/td>\s*<td[^>]*>([^<]*)<\/td>/);
          if (housingUnitsMatch && housingUnitsMatch[1].trim()) housingUnits = housingUnitsMatch[1].trim();
          detailByRequest.set(requestNum, { requestType, housingUnits });
        } catch (error) {
          console.warn('🏢 Failed to fetch detailed info for request', requestNum, error);
        }
      }));

      for (const row of rows) {
        const requestMatch = row.match(/getRequest\(([^)]+)\)/);
        const requestNumMatch = row.match(/>(\d+)</);
        const buildingFileMatch = row.match(/getBuilding\(([^)]+)\)/);
        const dateMatch = row.match(/(\d{2}\/\d{2}\/\d{4})/);
        const applicantMatch = row.match(/<td class="hidden-on-mobile">([^<]+)<\/td>/);
        const addressMatch = row.match(/<td>([^<]+רחובות[^<]*)<\/td>/);
        
        if (requestMatch && requestNumMatch) {
          const requestId = requestMatch[1];
          const requestNum = requestNumMatch[1];
          
          // Details were pre-fetched concurrently above (detailByRequest).
          const detail = detailByRequest.get(requestNum) || {};
          const requestType = detail.requestType || '';
          const housingUnits = detail.housingUnits || '';

          permits.push({
            id: `rehovot-${requestId}`,
            title: `בקשת היתר ${requestNum}`,
            status: 'מאושר', // Default status from historical permits
            statusType: 'approved',
            submissionDate: dateMatch ? dateMatch[1] : '',
            requestNumber: requestNum,
            requestId: requestId,
            buildingFile: buildingFileMatch ? buildingFileMatch[1] : '',
            applicant: applicantMatch ? applicantMatch[1].trim() : '',
            address: addressMatch ? addressMatch[1].trim() : '',
            requestType: requestType, // NEW: סוג הבקשה
            housingUnits: housingUnits, // NEW: סך מספר יחידות דיור המבוקשות
            municipality: 'Rehovot',
            dataSource: 'Rehovot Municipality',
            isMockData: false,
            isRehovotMunicipal: true,
            rehovotRequestId: requestId,
            websiteUrl: `https://rechovot.complot.co.il/iturbakashot/#request/${requestNum}`
          });
        }
      }

      console.log('🏢 Parsed Rehovot permits:', permits.length);

      return { permits, buildingData: null }; // Rehovot doesn't provide building details in listing

    } catch (error) {
      console.error('🏢 Error parsing Rehovot data:', error);
      return { permits: [], buildingData: null };
    }
  }

  /**
   * Get Rehovot specific status type for styling
   */
  static getRehovotStatusType(status) {
    if (!status) return 'approved'; // Default for historical permits
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('מאושר') || statusLower.includes('הוצא')) return 'approved';
    if (statusLower.includes('בבדיקה') || statusLower.includes('בטיפול')) return 'pending';
    if (statusLower.includes('נדחה') || statusLower.includes('מבוטל')) return 'rejected';
    
    return 'approved'; // Default
  }

  // =============================================================================
  // JERUSALEM MUNICIPALITY INTEGRATION
  // =============================================================================

  /**
   * Get Jerusalem municipal building permit data using gush/helka
   */
  static async getJerusalemMunicipalData(gush, helka) {
    try {
      console.log('🕌 ===== CALLING JERUSALEM MUNICIPAL API =====');
      console.log('🕌 Getting Jerusalem municipal data for:', { gush, helka });

      const requestBody = {
        ProcName: 242700437,
        Cnn: "cnnGisYk",
        Parameters: {
          misparTik: null,
          gush: parseInt(gush),
          helka: parseInt(helka),
          rehovCode: null,
          mispBait: null,
          mezahe: null,
          migrash: null,
          systemId: "26400046"
        }
      };

      console.log('🕌 Jerusalem API request body:', JSON.stringify(requestBody, null, 2));

      // Proxy through our backend. The browser cannot call Jerusalem's API
      // directly — its Akamai WAF returns 403 without CORS headers to
      // cross-origin browser requests, so the call is CORS-blocked on any IP.
      // Server-side it's a same-origin server→server request, which works.
      const response = await fetch(`${config.apiBaseUrl}/api/govmap-layers?action=jerusalem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ProcName: requestBody.ProcName,
          Cnn: 'cnnGisYk',
          Parameters: requestBody.Parameters,
        })
      });

      if (!response.ok) {
        throw new Error(`Jerusalem API failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🕌 Raw Jerusalem API response:', data);

      return await this.formatJerusalemPlansForUI(data);

    } catch (error) {
      console.error('🕌 Jerusalem municipal data error:', error);
      return { permits: [], buildingData: null };
    }
  }

  /**
   * Parse Jerusalem JSON response and format for UI
   */
  static async formatJerusalemPlansForUI(jsonData) {
    try {
      console.log('🕌 Parsing Jerusalem JSON response');
      const permits = [];
      
      if (!jsonData || !Array.isArray(jsonData)) {
        console.log('🕌 No valid data array in response');
        return { permits: [], buildingData: null };
      }
      
      console.log('🕌 Found records:', jsonData.length);

      // Pre-fetch every permit's details CONCURRENTLY. This used to be a
      // sequential `await getJerusalemPermitDetails()` inside the loop — one
      // round-trip per permit, back-to-back (N permits = N serial calls). Same
      // data, just parallel, so a 9-permit parcel resolves in ~1 round-trip.
      const detailsList = await Promise.all(
        jsonData.map((rec) => {
          const t = rec.tikNum || rec.tik_num;
          return t ? this.getJerusalemPermitDetails(t).catch(() => null) : Promise.resolve(null);
        })
      );

      for (let recordIndex = 0; recordIndex < jsonData.length; recordIndex++) {
        const record = jsonData[recordIndex];
        try {
          console.log('🕌 Processing Jerusalem record:', record);
          console.log('🕌 Jerusalem record field names:', Object.keys(record));
          console.log('🕌 Jerusalem record values:', record);
          
          // Show each field name and its value for debugging
          Object.keys(record).forEach(key => {
            console.log(`🕌 Field: ${key} = ${record[key]}`);
          });
          
          // Extract tikNum - handle both camelCase and underscore formats
          const tikNum = record.tikNum || record.tik_num;
          console.log('🕌 Extracted tikNum:', tikNum);
          
          // Extract the actual status text (not just the code) - handle multiple formats from logs
          const status = record.teurStatus || record.teur_status || 'לא ידוע';
          const statusDate = record.taarihStatus || record.taarich_status;
          const requestType = record.teurSugbakashaCodeMulti || record.teur_sugbakasha_code_multi || record.teurSugbakasha || '';
          const requestEssence = record.mahutBakasha || record.mahut_bakasha || '';
          const streetName = record.shemRehov || record.shem_rehov || '';
          const neighborhood = record.shemSchuna || record.shem_schuna || '';
          const buildingNumber = record.misparBait || record.mispar_bait;
          
          // Extract applicant info from baaleiInyanList (look for מבקש/מגיש)
          let applicant = '';
          if (record.baaleiInyanList && Array.isArray(record.baaleiInyanList)) {
            const applicantRecord = record.baaleiInyanList.find(item => 
              item.teurSugBaalInyan && item.teurSugBaalInyan.includes('מבקש'));
            if (applicantRecord) {
              applicant = `${applicantRecord.prati || ''} ${applicantRecord.mishpaha || ''}`.trim();
            }
          }
          
          // Build full address
          const fullAddress = [streetName, buildingNumber, neighborhood].filter(Boolean).join(' ');
          
          console.log('🕌 ====== JERUSALEM FIELD EXTRACTION DEBUG ======');
          console.log('🕌 Raw record.teurStatus:', record.teurStatus);
          console.log('🕌 Raw record.tikNum:', record.tikNum);
          console.log('🕌 Raw record.taarihStatus:', record.taarihStatus);
          console.log('🕌 Raw record.teurSugbakashaCodeMulti:', record.teurSugbakashaCodeMulti);
          console.log('🕌 Raw record.mahutBakasha:', record.mahutBakasha);
          console.log('🕌 ====== EXTRACTED VALUES ======');
          console.log('  - tikNum:', tikNum);
          console.log('  - status:', status);
          console.log('  - statusDate:', statusDate);
          console.log('  - requestType:', requestType);
          console.log('  - requestEssence:', requestEssence);
          console.log('  - address:', fullAddress);
          console.log('  - applicant:', applicant);
          console.log('🕌 ====== STATUS TYPE MAPPING ======');
          const mappedStatusType = this.getJerusalemStatusType(status);
          console.log('  - status input to mapper:', status);
          console.log('  - mapped statusType:', mappedStatusType);
          console.log('🕌 ===============================');
          
          // Details were pre-fetched concurrently above (see detailsList).
          const details = tikNum ? detailsList[recordIndex] : null;
          console.log('🕌 Details response for', tikNum, ':', details);
          
          console.log('🕌 ====== FINAL EXTRACTED VALUES FROM BASIC RECORD ======');
          console.log('  - Final Status:', status);
          console.log('  - Final Request Type:', requestType);
          console.log('  - Final Request Essence:', requestEssence);
          console.log('  - Final Address:', fullAddress);
          console.log('  - Final Applicant:', applicant);
          console.log('  - Final Area:', record.shetahAtar);
          console.log('  - Final Usage:', record.teurShimusCodehMulti);
          console.log('  - Final Location:', record.teurMakom);
          console.log('  - Final Department:', record.tzevet);
          console.log('  - Final Architect:', record.achrafikFullName);
          console.log('🕌 ===============================');
          
          // Now use BOTH basic record AND corrected details response
          const finalStatus = (details && details.teurStatus) || status || 'לא ידוע';
          const finalStatusType = this.getJerusalemStatusType(finalStatus);
          
          const formattedPermit = {
            id: `jerusalem-${tikNum || record.id || Date.now()}`,
            title: tikNum || 'מספר תיק לא זמין',
            
            // Prefer details response, fallback to basic record
            status: finalStatus,
            statusType: finalStatusType,
            date: (details && details.taarihStatus) || statusDate || null,
            statusDate: (details && details.taarihStatus) || statusDate || null,
            
            // Rich description - prefer details
            description: details && details.mahutBakasha ? 
              details.mahutBakasha : 
              (requestType && requestEssence ? `${requestType} - ${requestEssence}` : requestType || requestEssence || ''),
            
            requestType: (details && details.teurSugbakashaCodeMulti) || requestType || '',
            requestEssence: (details && details.mahutBakasha) || requestEssence || '',
            
            // Address info - prefer details
            address: details ? 
              [details.shemRehov, details.misparBait, details.shemSchuna].filter(Boolean).join(' ') : 
              fullAddress,
            streetName: (details && details.shemRehov) || streetName || '',
            neighborhood: (details && details.shemSchuna) || neighborhood || '',
            buildingNumber: (details && details.misparBait) || buildingNumber || '',
            
            // Extract applicant - prefer details
            applicant: details && details.baaleiInyanList ? 
              this.extractJerusalemApplicant(details.baaleiInyanList) : 
              applicant,
            
            // Area and usage information - prefer details
            area: (details && details.shetahAtar) || record.shetahAtar || record.shetah_atar || null,
            usage: (details && details.teurShimusCodehMulti) || record.teurShimusCodehMulti || record.shimushCodeMulti || '',
            location: (details && details.teurMakom) || record.teurMakom || '',
            
            // Team/department info - prefer details
            department: (details && details.tzevet) || record.tzevet || '',
            
            permitType: (details && details.teurSugbakashaCodeMulti) || requestType || '',
            
            // Professional details - prefer details
            architect: details && details.achraTikFullName ? 
              `${details.achraTikFullName} ${details.achraTik ? '(' + details.achraTik + ')' : ''}`.trim() : 
              (record.achrafikFullName ? `${record.achrafikFullName} ${record.achrafik ? '(' + record.achrafik + ')' : ''}`.trim() : ''),
            
            // Additional details from details response
            permitStatus: details && details.maslulTik ? details.maslulTik : '',
            detailedDescription: details && details.teur ? details.teur : '', // HTML content
            
            // Data source identification
            source: 'jerusalem-municipal',
            isJerusalemMunicipal: true,
            isRealData: true,
            jerusalemTikNum: tikNum,
            websiteUrl: tikNum ? `https://ykpubdata.jerusalem.muni.il/#/Details?TikNum=${tikNum}&Page=BakashalInfo` : null,
            
            rawRecord: record,
            detailsResponse: details // Store the corrected details response
          };
          
          permits.push(formattedPermit);
          console.log('🕌 ====== FINAL FORMATTED PERMIT ======');
          console.log('🕌 Added Jerusalem permit:', formattedPermit.title);
          console.log('🕌 Status:', formattedPermit.status, '| Type:', formattedPermit.statusType);
          console.log('🕌 Description:', formattedPermit.description);
          console.log('🕌 Request Type:', formattedPermit.requestType);
          console.log('🕌 Request Essence:', formattedPermit.requestEssence);
          console.log('🕌 Address:', formattedPermit.address);
          console.log('🕌 Applicant:', formattedPermit.applicant);
          console.log('🕌 Usage:', formattedPermit.usage);
          console.log('🕌 Location:', formattedPermit.location);
          console.log('🕌 Department:', formattedPermit.department);
          console.log('🕌 Architect:', formattedPermit.architect);
          console.log('🕌 ===============================');
          
        } catch (detailError) {
          // Extract tikNum and other fields using robust field names (handle both formats)
          const tikNum = record.tikNum || record.tik_num;
          console.warn('🕌 Error getting details for permit:', tikNum, detailError);
          
          // Use the same robust extraction logic as above
          const status = record.teurStatus || record.teur_status || 'לא ידוע';
          const statusDate = record.taarihStatus || record.taarich_status;
          const requestType = record.teurSugbakashaCodeMulti || record.teur_sugbakasha_code_multi || record.teurSugbakasha || '';
          const requestEssence = record.mahutBakasha || record.mahut_bakasha || '';
          const streetName = record.shemRehov || record.shem_rehov || '';
          const neighborhood = record.shemSchuna || record.shem_schuna || '';
          const buildingNumber = record.misparBait || record.mispar_bait;
          
          // Extract applicant info from baaleiInyanList
          let applicant = '';
          if (record.baaleiInyanList && Array.isArray(record.baaleiInyanList)) {
            const applicantRecord = record.baaleiInyanList.find(item => 
              item.teurSugBaalInyan && item.teurSugBaalInyan.includes('מבקש'));
            if (applicantRecord) {
              applicant = `${applicantRecord.prati || ''} ${applicantRecord.mishpaha || ''}`.trim();
            }
          }
          
          // Build full address
          const fullAddress = [streetName, buildingNumber, neighborhood].filter(Boolean).join(' ');
          
          console.log('🕌 ====== ERROR PATH - JERUSALEM FIELD EXTRACTION DEBUG ======');
          console.log('🕌 Raw record.teurStatus:', record.teurStatus);
          console.log('🕌 Extracted status in error path:', status);
          const mappedStatusTypeError = this.getJerusalemStatusType(status);
          console.log('🕌 Mapped statusType in error path:', mappedStatusTypeError);
          console.log('🕌 ===============================');
          
          // Still add the basic permit using rich data from basic record
          permits.push({
            id: `jerusalem-${tikNum || record.id || Date.now()}`,
            title: tikNum || 'מספר תיק לא זמין',
            status: status,
            statusType: mappedStatusTypeError,
            date: statusDate || null,
            statusDate: statusDate || null,
            description: requestType && requestEssence ? 
              `${requestType} - ${requestEssence}` : 
              requestType || requestEssence || '',
            requestType: requestType,
            requestEssence: requestEssence,
            address: fullAddress,
            applicant: applicant,
            streetName: streetName,
            neighborhood: neighborhood,
            buildingNumber: buildingNumber,
            permitType: requestType,
            area: record.shetahAtar || record.shetah_atar || null,
            usage: record.teurShimusCodehMulti || record.shimushCodeMulti || '',
            location: record.teurMakom || '',
            department: record.tzevet || '',
            architect: record.achrafikFullName ? 
              `${record.achrafikFullName} ${record.achrafik ? '(' + record.achrafik + ')' : ''}`.trim() : '',
            source: 'jerusalem-municipal',
            isJerusalemMunicipal: true,
            isRealData: true,
            jerusalemTikNum: tikNum,
            websiteUrl: tikNum ? `https://ykpubdata.jerusalem.muni.il/#/Details?TikNum=${tikNum}&Page=BakashalInfo` : null,
            
            rawRecord: record
          });
        }
      }
      
      return { permits, buildingData: null }; // Jerusalem doesn't provide building data like Tel Aviv
      
    } catch (error) {
      console.error('🕌 Error parsing Jerusalem data:', error);
      return { permits: [], buildingData: null };
    }
  }

  // =============================================================================
  // COMPLOT MUNICIPAL DATA (15 municipalities)
  // =============================================================================

  // Complot municipalities configuration - 27 municipalities supported!
  static COMPLOT_MUNICIPALITIES = {
    // siteid: 2
    'maalot': { siteid: 2, nameHe: 'מעלות תרשיחא', nameEn: "Ma'alot Tarshicha" },
    // siteid: 3
    'ramat-gan': { siteid: 3, nameHe: 'רמת גן', nameEn: 'Ramat Gan', subdomain: 'handasa.ramat-gan.muni.il' },
    // siteid: 10
    'tiberias': { siteid: 10, nameHe: 'טבריה', nameEn: 'Tiberias' },
    // siteid: 11
    'dimona': { siteid: 11, nameHe: 'דימונה', nameEn: 'Dimona', subdomain: 'dimona.complot.co.il' },
    // siteid: 12
    'safed': { siteid: 12, nameHe: 'צפת', nameEn: 'Safed' },
    // siteid: 118
    'ramat-hasharon': { siteid: 118, nameHe: 'רמת השרון', nameEn: 'Ramat HaSharon' },
    // siteid: 34
    'holon': { siteid: 34, nameHe: 'חולון', nameEn: 'Holon' },
    // siteid: 84
    'petah-tikva': { siteid: 84, nameHe: 'פתח תקווה', nameEn: 'Petah Tikva' },
    // siteid: 121
    'herzliya': { siteid: 121, nameHe: 'הרצליה', nameEn: 'Herzliya' },
    // siteid: 15
    'raanana': { siteid: 15, nameHe: 'רעננה', nameEn: "Ra'anana", subdomain: 'www.raanana.muni.il' },
    // siteid: 16
    'haifa': { siteid: 16, nameHe: 'חיפה', nameEn: 'Haifa', subdomain: 'haifa.complot.co.il' },
    // siteid: 22
    'rehovot': { siteid: 22, nameHe: 'רחובות', nameEn: 'Rehovot', subdomain: 'rechovot.complot.co.il' },
    // siteid: 24
    'tira': { siteid: 24, nameHe: 'טירה', nameEn: 'Tira' },
    // siteid: 31
    'or-yehuda': { siteid: 31, nameHe: 'אור יהודה', nameEn: 'Or Yehuda' },
    // siteid: 32
    'yokneam': { siteid: 32, nameHe: 'יקנעם עילית', nameEn: 'Yokneam' },
    // siteid: 33
    'hod-hasharon': { siteid: 33, nameHe: 'הוד השרון', nameEn: 'Hod HaSharon', subdomain: 'hodhasharon.complot.co.il' },
    // siteid: 38
    'sderot': { siteid: 38, nameHe: 'שדרות', nameEn: 'Sderot', subdomain: 'sderot.complot.co.il' },
    // siteid: 46
    'kiryat-gat': { siteid: 46, nameHe: 'קרית גת', nameEn: 'Kiryat Gat', subdomain: 'qiryat-gat.complot.co.il' },
    // siteid: 49
    'kfar-saba': { siteid: 49, nameHe: 'כפר סבא', nameEn: 'Kfar Saba' },
    // siteid: 56
    'eilat': { siteid: 56, nameHe: 'אילת', nameEn: 'Eilat', subdomain: 'eilat.complot.co.il' },
    // siteid: 67
    'ofakim': { siteid: 67, nameHe: 'אופקים', nameEn: 'Ofakim' },
    // siteid: 75
    'bnei-brak': { siteid: 75, nameHe: 'בני ברק', nameEn: 'Bnei Brak' },
    // siteid: 78
    'ashdod': { siteid: 78, nameHe: 'אשדוד', nameEn: 'Ashdod', subdomain: 'ashdod.complot.co.il' },
    // siteid: 80
    'nahariya': { siteid: 80, nameHe: 'נהריה', nameEn: 'Nahariya', subdomain: 'nahariya.complot.co.il' },
    // siteid: 81
    'bat-yam': { siteid: 81, nameHe: 'בת ים', nameEn: 'Bat Yam', subdomain: 'batyam.complot.co.il' },
    // siteid: 82
    'modiin': { siteid: 82, nameHe: 'מודיעין', nameEn: "Modi'in", subdomain: 'modiin.complot.co.il' },
    // siteid: 87
    'yavne': { siteid: 87, nameHe: 'יבנה', nameEn: 'Yavne', subdomain: 'yavne.complot.co.il' },
    // siteid: 93
    'beit-shemesh': { siteid: 93, nameHe: 'בית שמש', nameEn: 'Beit Shemesh' },
    // siteid: 95
    'ashkelon': { siteid: 95, nameHe: 'אשקלון', nameEn: 'Ashkelon', subdomain: 'ashkelon.complot.co.il' },
    // siteid: 98
    'givatayim': { siteid: 98, nameHe: 'גבעתיים', nameEn: 'Givatayim', subdomain: 'handasi.complot.co.il' },
    // siteid: 105
    'beer-sheva': { siteid: 105, nameHe: 'באר שבע', nameEn: 'Beer Sheva' },
    // siteid: 108
    'rishon-lezion': { siteid: 108, nameHe: 'ראשון לציון', nameEn: 'Rishon LeZion', subdomain: 'rishonlezion.complot.co.il' },
    // siteid: 123
    'nazareth': { siteid: 123, nameHe: 'נצרת', nameEn: 'Nazareth', subdomain: 'nazareth.complot.co.il' },
  };

  /**
   * Detect Complot municipality from address
   */
  static detectComplotMunicipality(address) {
    if (!address) return null;
    const addressLower = address.toLowerCase();
    
    for (const [key, cfg] of Object.entries(this.COMPLOT_MUNICIPALITIES)) {
      // Check Hebrew name
      if (address.includes(cfg.nameHe)) {
        return { key, ...cfg };
      }
      // Check English name
      if (addressLower.includes(cfg.nameEn.toLowerCase())) {
        return { key, ...cfg };
      }
      // Check key variations
      if (addressLower.includes(key.replace(/-/g, ' ')) || addressLower.includes(key.replace(/-/g, ''))) {
        return { key, ...cfg };
      }
    }
    return null;
  }

  /**
   * Get municipal planning data from any Complot municipality
   * Works with all 27 supported municipalities across Israel!
   */
  static async getComplotMunicipalPlans(gush, helka, cityKey) {
    try {
      const cfg = this.COMPLOT_MUNICIPALITIES[cityKey];
      if (!cfg) {
        console.log(`⚠️ Unknown Complot municipality: ${cityKey}`);
        return { plans: [] };
      }

      console.log(`🏛️ ===== CALLING ${cfg.nameEn.toUpperCase()} MUNICIPAL API VIA VERCEL FUNCTION =====`);
      console.log(`🏛️ Getting ${cfg.nameHe} plans for:`, { gush, helka, siteid: cfg.siteid });

      const response = await fetch(`${config.apiBaseUrl}/api/municipal-1?city=${cityKey}&gush=${gush}&helka=${helka}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        console.log(`⚠️ ${cfg.nameHe} API returned status:`, response.status);
        return { plans: [] };
      }

      const data = await response.json();
      console.log(`🏛️ ${cfg.nameHe} API response:`, data);

      if (!data) {
        console.log(`⚠️ No ${cfg.nameHe} response data`);
        return { plans: [], permits: [], buildingData: null };
      }

      // Format plans to match our structure
      const formattedPlans = (data.plans || []).map(plan => ({
        id: `${cityKey}-${plan.planNumber || Math.random()}`,
        title: plan.description || plan.planNumber || `תוכנית ${cfg.nameHe}`,
        status: plan.status || 'לא ידוע',
        statusType: this.getComplotStatusType(plan.status),
        submissionDate: plan.statusDate || null,
        approvalDate: plan.statusDate && plan.status?.includes('תוקף') ? plan.statusDate : null,
        planNumber: plan.planNumber || '',
        planUrl: plan.planUrl || data.url || `https://${cfg.subdomain}/gush2/#gush/${gush}/${helka}`,
        architect: null,
        planType: 'תוכנית מקומית',
        area: null,
        landUse: null,
        county: cfg.nameHe,
        objectives: null,
        planSubtype: null,
        permitType: 'תוכנית בניה',
        approvingAuthority: 'רשות מקומית',
        location: cfg.nameHe,
        canIssuePermits: false,
        isRealData: true,
        source: `${cityKey}_municipal`,
        municipalSource: cfg.nameHe,
        municipality: cfg.nameHe, // Required for categorization in ResultsPage
        isComplotMunicipal: true,
        complotSiteid: cfg.siteid
      }));

      console.log(`🏛️ Formatted ${formattedPlans.length} ${cfg.nameHe} municipal plans`);

      // Also include permits if returned by the API
      const permits = data.permits || [];
      if (permits.length > 0) {
        console.log(`🏗️ Found ${permits.length} ${cfg.nameHe} building permits`);
      }

      return {
        plans: formattedPlans,
        permits: permits,
        buildingData: data.buildingData || null,
        municipality: cfg.nameHe,
        siteid: cfg.siteid
      };

    } catch (error) {
      console.error(`🏛️ Complot municipal data error (${cityKey}):`, error);
      return { plans: [], permits: [], buildingData: null };
    }
  }

  /**
   * Legacy method - calls the new unified method for backward compatibility
   */
  static async getRamatGanMunicipalPlans(gush, helka) {
    return this.getComplotMunicipalPlans(gush, helka, 'ramat-gan');
  }

  /**
   * Get Complot status type for styling
   */
  static getComplotStatusType(status) {
    if (!status) return 'pending';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('בתוקף') || statusLower.includes('מאושר') || statusLower.includes('אושר')) return 'approved';
    if (statusLower.includes('בבדיקה') || statusLower.includes('בטיפול') || statusLower.includes('תלוי')) return 'pending';
    if (statusLower.includes('נדחה') || statusLower.includes('מבוטל')) return 'rejected';
    return 'approved'; // Default - most plans in tables are approved
  }

  /**
   * Legacy method alias
   */
  static getRamatGanStatusType(status) {
    return this.getComplotStatusType(status);
  }

  /**
   * Check if address is in a Complot municipality (excluding those with dedicated handlers)
   */
  static isAddressInComplotMunicipality(address) {
    // Exclude cities that have their own dedicated handlers (Tel Aviv, Jerusalem, Rehovot has special permit handling)
    const municipality = this.detectComplotMunicipality(address);
    return municipality !== null;
  }

  // =============================================================================
  // JERUSALEM MUNICIPAL DATA
  // =============================================================================

  /**
   * Get detailed information for a specific Jerusalem permit
   */
  static async getJerusalemPermitDetails(tikNum) {
    try {
      console.log('🕌 Getting details for Jerusalem permit:', tikNum);
      
      const requestBody = {
        ProcName: 242700447,
        Cnn: "cnnGisYk",
        Parameters: {
          tikNum: tikNum,
          systemCode: "26400046"
        }
      };

      console.log('🕌 Jerusalem details API request body:', JSON.stringify(requestBody, null, 2));

      // Proxy through our backend. The browser cannot call Jerusalem's API
      // directly — its Akamai WAF returns 403 without CORS headers to
      // cross-origin browser requests, so the call is CORS-blocked on any IP.
      // Server-side it's a same-origin server→server request, which works.
      const response = await fetch(`${config.apiBaseUrl}/api/govmap-layers?action=jerusalem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ProcName: requestBody.ProcName,
          Cnn: 'cnnGisYk',
          Parameters: requestBody.Parameters,
        })
      });

      if (!response.ok) {
        console.error('🕌 Jerusalem details API failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('🕌 Jerusalem details API error response:', errorText);
        throw new Error(`Jerusalem details API failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🕌 Jerusalem permit details response:', data);

      if (data && Array.isArray(data) && data.length > 0) {
        const details = data[0];
        console.log('🕌 Processing Jerusalem details with ACTUAL fields:', Object.keys(details));
        return {
          // Use the ACTUAL field names from the API response you showed me
          tikNum: details.tikNum,
          teurStatus: details.teurStatus,
          teurSugbakashaCodeMulti: details.teurSugbakashaCodeMulti,
          teurShimusCodehMulti: details.teurShimusCodehMulti, 
          mahutBakasha: details.mahutBakasha,
          shetahAtar: details.shetahAtar,
          taarihStatus: details.taarihStatus,
          maslulTik: details.maslulTik,
          misparBait: details.misparBait,
          shemRehov: details.shemRehov,
          shemSchuna: details.shemSchuna,
          baaleiInyanList: details.baaleiInyanList,
          tzevet: details.tzevet,
          achraTikFullName: details.achraTikFullName,
          achraTik: details.achraTik,
          teur: details.teur, // HTML description content
          teurMakom: details.teurMakom,
          ezorRishuiCode: details.ezorRishuiCode,
          fullTaarihStatus: details.fullTaarihStatus,
          statusCode: details.statusCode,
          websiteUrl: `https://ykpubdata.jerusalem.muni.il/#/Details?TikNum=${tikNum}&Page=BakashalInfo`
        };
      }

      return null;
    } catch (error) {
      console.warn('🕌 Error getting Jerusalem permit details:', error);
      return null;
    }
  }

  /**
   * Extract applicant information from Jerusalem baaleiInyanList
   */
  static extractJerusalemApplicant(baaleiInyanList) {
    if (!baaleiInyanList || !Array.isArray(baaleiInyanList)) {
      return '';
    }
    
    const applicantRecord = baaleiInyanList.find(item => 
      item.teurSugBaalInyan && item.teurSugBaalInyan.includes('מבקש'));
    
    if (applicantRecord) {
      return `${applicantRecord.prati || ''} ${applicantRecord.mishpaha || ''}`.trim();
    }
    
    return '';
  }

  /**
   * Map Jerusalem permit status to generic status type
   */
  static getJerusalemStatusType(status) {
    if (!status || typeof status !== 'string') {
      console.log('🕌 Status is empty or not a string:', status);
      return 'unknown';
    }
    
    const statusLower = status.toLowerCase();
    console.log('🕌 Mapping Jerusalem status:', status, 'lowercase:', statusLower);
    
    // Jerusalem-specific status mappings based on actual API responses
    if (statusLower.includes('נסגרה') || statusLower.includes('סגור') || statusLower.includes('הושלם')) return 'completed';
    if (statusLower.includes('מאושר') || statusLower.includes('אושר') || statusLower.includes('תקף')) return 'approved';
    if (statusLower.includes('נדחה') || statusLower.includes('דחה') || statusLower.includes('בוטל')) return 'rejected';
    if (statusLower.includes('ממתין') || statusLower.includes('בבדיקה') || statusLower.includes('בטיפול') || statusLower.includes('פתוח')) return 'pending';
    
    // English status mappings as fallback
    if (statusLower.includes('closed') || statusLower.includes('completed') || statusLower.includes('finished')) return 'completed';
    if (statusLower.includes('approved') || statusLower.includes('valid') || statusLower.includes('active')) return 'approved';
    if (statusLower.includes('rejected') || statusLower.includes('denied') || statusLower.includes('cancelled')) return 'rejected';
    if (statusLower.includes('pending') || statusLower.includes('review') || statusLower.includes('processing')) return 'pending';
    
    console.log('🕌 No specific mapping found for status:', status, '- defaulting to completed');
    return 'completed'; // Default since "הבקשה נסגרה" means "request closed/completed"
  }

  /**
   * OPTIMIZATION: Start all municipal data fetches in parallel
   * Returns array of promises that resolve with municipal data
   */
  static startMunicipalFetches(addressForCityDetection, coordinates, parcelCoords, gush, helka, onStatusUpdate) {
    const fetches = [];
    
    // Tel Aviv (has its own dedicated GIS system)
    const isTelAvivAddress = addressForCityDetection ? this.isAddressInTelAviv(addressForCityDetection) : false;
    const isTelAvivCoords = coordinates ? this.isInTelAviv(coordinates.x, coordinates.y) : false;
    const isTelAvivParcel = parcelCoords ? this.isInTelAviv(parcelCoords.x, parcelCoords.y) : false;
    
    if (isTelAvivAddress || (coordinates && isTelAvivCoords) || isTelAvivParcel) {
      console.log('🏛️ Queueing Tel Aviv municipal data fetch');
      const coords = coordinates || parcelCoords;
      if (coords) {
        fetches.push(
          this.getTelAvivMunicipalData(coords.x, coords.y)
            .then(data => ({ type: 'tel_aviv', data }))
            .catch(error => {
              console.error('❌ Tel Aviv fetch error:', error);
              return { type: 'tel_aviv', data: null };
            })
        );
      }
    }
    
    // Jerusalem (has its own dedicated system)
    const isJerusalemAddress = addressForCityDetection ? this.isAddressInJerusalem(addressForCityDetection) : false;
    const isJerusalemCoords = coordinates ? this.isInJerusalem(coordinates.x, coordinates.y) : false;
    const isJerusalemParcel = parcelCoords ? this.isInJerusalem(parcelCoords.x, parcelCoords.y) : false;
    
    if (isJerusalemAddress || (coordinates && isJerusalemCoords) || isJerusalemParcel) {
      console.log('🕌 Queueing Jerusalem municipal data fetch');
      fetches.push(
        this.getJerusalemMunicipalData(gush, helka)
          .then(data => ({ type: 'jerusalem', data }))
          .catch(error => {
            console.error('❌ Jerusalem fetch error:', error);
            return { type: 'jerusalem', data: null };
          })
      );
    }
    
    // Complot municipalities (15 cities - includes Ramat Gan, Rehovot, Haifa, etc.)
    const complotMunicipality = addressForCityDetection ? this.detectComplotMunicipality(addressForCityDetection) : null;
    
    if (complotMunicipality) {
      console.log(`🏛️ Queueing ${complotMunicipality.nameHe} (Complot siteid=${complotMunicipality.siteid}) municipal plans fetch`);
      fetches.push(
        this.getComplotMunicipalPlans(gush, helka, complotMunicipality.key)
          .then(data => ({ type: 'complot', data, municipality: complotMunicipality }))
          .catch(error => {
            console.error(`❌ ${complotMunicipality.nameHe} fetch error:`, error);
            return { type: 'complot', data: null, municipality: complotMunicipality };
          })
      );
      
      // Special handling for Rehovot - also fetch permits via dedicated method
      if (complotMunicipality.key === 'rehovot') {
        console.log('🏢 Queueing Rehovot permits (additional to Complot plans)');
        fetches.push(
          this.getRehovotMunicipalData(gush, helka)
            .then(data => ({ type: 'rehovot', data }))
            .catch(error => {
              console.error('❌ Rehovot permits fetch error:', error);
              return { type: 'rehovot', data: null };
          })
      );
      }
    }
    
    return fetches;
  }

  /**
   * OPTIMIZATION: Clear the parcel geometry cache (useful for testing or memory management)
   */
  static clearParcelGeometryCache() {
    parcelGeometryCache.clear();
    console.log('🗑️ Parcel geometry cache cleared');
  }

  /**
   * OPTIMIZATION: Get cache stats for monitoring
   */
  static getParcelGeometryCacheStats() {
    return {
      size: parcelGeometryCache.size,
      keys: Array.from(parcelGeometryCache.keys())
    };
  }
}

export default ArchitecturalPlansService;
